import { Menu, Transition, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';
import clsx from 'clsx';
import {
  Fragment,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import SearchBar from '~/shared/components/SearchBar';
import Tag, { type TagVariant } from '~/shared/components/Tag';
import Tooltip from '~/shared/components/Tooltip';
import Typography from '~/shared/components/Typography';

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

const navigationKeys = [
  'ArrowDown',
  'ArrowUp',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Escape',
  'Tab',
  'Enter',
];

interface SimpleDropdownProps<T extends string = string> {
  items: T[];
  selectedItem: T;
  onSelect: (item: T) => void;
  align?: 'top-right' | 'top-left';
  className?: string;
  menuButtonClassName?: string;
  menuItemClassName?: string;
  menuItemsClassName?: string;
  disabled?: boolean;
  'aria-label'?: string;
  displayLabel?: string;
  getItemLabel?: (item: T) => string;
  isItemSelected?: (item: T) => boolean;
  renderItemEnd?: (item: T) => ReactNode;
  renderButtonEnd?: () => ReactNode;
  fullWidth?: boolean;
  portal?: boolean;
  menuButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  onMenuButtonClick?: () => void;
  menuButtonLabelClassName?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsLabel?: string;
  showItemTooltip?: boolean;
  tooltipIdPrefix?: string;
  itemTagVariant?: TagVariant;
  menuListClassName?: string;
  showItemsBorderBottom?: boolean;
  chevronDownIconClassName?: string;
}

export default function SimpleDropdown<T extends string = string>({
  items,
  selectedItem,
  onSelect,
  align = 'top-right',
  className,
  menuButtonClassName,
  menuItemClassName,
  menuItemsClassName,
  disabled = false,
  'aria-label': ariaLabel,
  displayLabel,
  getItemLabel,
  isItemSelected,
  renderItemEnd,
  renderButtonEnd,
  fullWidth = false,
  portal = false,
  menuButtonRef,
  onMenuButtonClick,
  menuButtonLabelClassName,
  searchable = false,
  searchPlaceholder = 'Search',
  noResultsLabel = 'No results found',
  showItemTooltip = false,
  tooltipIdPrefix,
  itemTagVariant,
  menuListClassName,
  showItemsBorderBottom = true,
  chevronDownIconClassName,
}: SimpleDropdownProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const checkSelected = isItemSelected ?? ((item: T) => item === selectedItem);
  const buttonLabel = displayLabel ?? getItemLabel?.(selectedItem) ?? selectedItem;
  const itemLabel = getItemLabel ?? ((item: T) => item);
  const menuButtonAriaLabel = ariaLabel ? `${ariaLabel}, ${buttonLabel}` : buttonLabel;
  const buttonTooltipId =
    showItemTooltip && tooltipIdPrefix ? `${tooltipIdPrefix}-button` : undefined;

  const visibleItems = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return items;
    }
    const query = searchQuery.trim().toLowerCase();
    return items.filter((item) => itemLabel(item).toLowerCase().includes(query));
  }, [items, searchable, searchQuery, itemLabel]);

  const itemIndexById = useMemo(() => {
    const map = new Map<T, number>();
    items.forEach((item, index) => map.set(item, index));
    return map;
  }, [items]);

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!navigationKeys.includes(e.key)) {
      e.stopPropagation();
    }
  };

  const menuItems = (
    <Transition
      as={Fragment}
      {...transitionProps}
      afterEnter={() => {
        if (searchable) {
          searchInputRef.current?.focus();
        }
      }}
      afterLeave={() => {
        if (searchable) {
          setSearchQuery('');
        }
      }}
    >
      <MenuItems
        anchor={portal ? { to: 'bottom start', gap: 8 } : undefined}
        className={clsx(
          portal
            ? 'rounded-lg bg-white z-[99999]'
            : 'absolute top-[100%] rounded-lg mt-2 origin-top-right bg-white z-30',
          !portal && (fullWidth ? 'w-full' : 'w-full'),
          searchable ? 'shadow-searchable-dropdown' : 'shadow-filter-dropdown',
          'focus-outline-none border-primary-70',
          'border border-primary-70 focus:outline-hidden',
          !portal &&
            (align === 'top-right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left'),
          searchable && 'flex flex-col',
          menuItemsClassName
        )}
      >
        <div
          className={clsx(
            'p3',
            portal && 'min-w-0 w-full',
            searchable && 'flex flex-1 flex-col min-h-0 overflow-hidden rounded-lg'
          )}
        >
          {searchable && (
            <div className="shrink-0 bg-white p-2" onClick={(e) => e.stopPropagation()}>
              <SearchBar
                ref={searchInputRef}
                size="sm"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full"
              />
            </div>
          )}
          <div className={clsx(searchable && 'flex-1 min-h-0 overflow-y-auto', menuListClassName)}>
            {visibleItems.length > 0 ? (
              visibleItems.map((item) => {
                const selected = checkSelected(item);
                const label = itemLabel(item);
                const rowTooltipId =
                  showItemTooltip && tooltipIdPrefix
                    ? `${tooltipIdPrefix}-item-${itemIndexById.get(item)}`
                    : undefined;

                return (
                  <Fragment key={item}>
                    <MenuItem
                      as={'button'}
                      className={(bag: { active: boolean }) => {
                        // Only the current selection gets the full border; hover and
                        // keyboard navigation are conveyed by the fill alone.
                        // The selection also keeps its fill at rest, not only while
                        // headless-ui marks it active.
                        const showFill = selected || bag.active;
                        return clsx(
                          'text-sm leading-6 w-full min-w-0 h-11 flex items-center justify-between px-5 py-[18px] border-gray-200 last:rounded-b-lg cursor-pointer group scroll-mb-2',
                          itemTagVariant && 'px-2.5!',
                          visibleItems[0] === item && !searchable && 'rounded-t-lg',
                          showFill && 'bg-primary-20',
                          selected && 'border border-primary-500',
                          !selected &&
                            item !== visibleItems[visibleItems.length - 1] &&
                            showItemsBorderBottom &&
                            'border-b border-gray-200',
                          menuItemClassName
                        );
                      }}
                      onClick={() => {
                        onSelect?.(item);
                      }}
                    >
                      {itemTagVariant ? (
                        <Tag
                          variant={itemTagVariant}
                          text={label}
                          dataTooltipId={rowTooltipId}
                          className="min-w-0"
                          truncateToContainer
                        />
                      ) : (
                        <div data-tooltip-id={rowTooltipId} className="min-w-0 flex-1 text-left">
                          <Typography
                            color="text-primary-900"
                            variant="p3-reg"
                            truncate
                            className="min-w-0"
                            title={rowTooltipId ? undefined : label}
                          >
                            {label}
                          </Typography>
                        </div>
                      )}
                      {renderItemEnd?.(item)}
                    </MenuItem>
                    {rowTooltipId && <Tooltip id={rowTooltipId} content={label} place="top" />}
                  </Fragment>
                );
              })
            ) : searchable ? (
              <Typography className="p-2 text-center text-gray-300" variant="caption">
                {noResultsLabel}
              </Typography>
            ) : null}
          </div>
        </div>
      </MenuItems>
    </Transition>
  );

  return (
    <div className={clsx('relative', fullWidth && 'w-full', className)}>
      <Menu as="div" className={clsx('relative flex', fullWidth && 'w-full')}>
        {({ open }) => (
          <>
            <MenuButton
              ref={(el) => {
                if (menuButtonRef) {
                  menuButtonRef.current = el;
                }
              }}
              disabled={disabled}
              aria-label={menuButtonAriaLabel}
              className={clsx(
                'bg-white text-primary-900 border border-gray-300 flex justify-between rounded-[8px] px-3 h-9 h-11 text-[14px] w-full',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                !disabled && open
                  ? 'bg-primary-20 border-primary-200'
                  : !disabled && 'hover:border-primary-500 active:border-primary-100',
                'focus:border-primary-300 focus:outline-none',
                menuButtonClassName
              )}
              onClick={onMenuButtonClick}
            >
              <div className="flex items-center justify-between w-full min-w-0 gap-2">
                <div data-tooltip-id={buttonTooltipId} className="min-w-0 flex-1 text-left">
                  <Typography
                    variant="p3-reg"
                    color="text-gray-700"
                    truncate
                    className={clsx('min-w-0', menuButtonLabelClassName)}
                    title={buttonTooltipId ? undefined : buttonLabel}
                  >
                    {buttonLabel}
                  </Typography>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {renderButtonEnd?.()}
                  <ChevronDownIcon
                    className={clsx(
                      'w-[14px] h-[14px] text-primary-900 stroke-3 cursor-pointer',
                      chevronDownIconClassName ?? '',
                      open ? 'rotate-180' : ''
                    )}
                  />
                </div>
              </div>
            </MenuButton>

            {portal ? <Portal>{menuItems}</Portal> : menuItems}
          </>
        )}
      </Menu>
      {buttonTooltipId && <Tooltip id={buttonTooltipId} content={buttonLabel} place="top" />}
    </div>
  );
}
